// Visualisations/moment/ScaleMomentVisualisation.jsx
import { useFirestoreID } from "../../../firebase/useFirestore";
import Tooltip from "../../common/Tooltip";
import AnalyticsEmptyState from "../../dashboard/AnalyticsEmptyState";
import useChartColors from '../../../hooks/useChartColors';
import useSettings from "../../../hooks/Settings";
import scaleStartLabel from "../../../helpers/questionnaire/scaleStartLabel";

const ScaleMomentVisualisation = ({ data, field, projectHierarchy, projectAverages, source = 'questionnaire', primaryColorOverride }) => {
  // palette[0]/[1] zijn de huisstijl binnen een live rapportage en de vaste
  // paletkleuren daarbuiten — zie docs/patterns.md#ui-chart-palette.
  const { palette } = useChartColors();
  // useSettings blijft nodig voor `portfolio` — dat is geen kleur.
  const settings = useSettings();
  const primaryColor = palette[0];
  const secundaryColor = palette[1];
  const h1Color = primaryColorOverride || (source === 'event' ? secundaryColor : primaryColor);
  // Per-project results are portfolio-only — child projects don't exist elsewhere.
  const showProjectResults = settings?.portfolio === 'block' && Boolean(projectHierarchy) && Boolean(projectAverages);

  const collectionName = source === 'event' ? 'EventFields' : 'QuestionnaireFields';
  const fields = useFirestoreID(collectionName, field || ""); // ← switched

  const calculateWidthPercentage = (averageScore, reachStart, reachEnd) => {
    const range = reachEnd - reachStart || 1;
    const normalized = ((averageScore - reachStart) / range) * 100;
    return Math.max(0, Math.min(100, normalized));
  };

  const projectResults = () => {
    if (!projectHierarchy || !projectAverages) return null;
    const sortedProjects = Object.entries(projectHierarchy).sort(
      ([, a], [, b]) => (a?.path || '').localeCompare(b?.path || '')
    );
    return (
      <div className="scale-visualisation-project-results-container" style={{ whiteSpace: 'nowrap' }}>
        {sortedProjects.map(([name, info], idx) => (
          <div key={idx} style={{ paddingLeft: `${info.depth * 10}px` }}>
            <p>{name}: {projectAverages[name] ?? '–'}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {data?.groupedData?.length > 0 ? (
        <>
          <div id="scale-visualisation-title-container">
            <h2>Gemiddelde score</h2>
          </div>

          <div id="scale-visualisation-scale-container">
            {fields && fields.map((fieldItem, index) => {
              const averageScore = parseFloat(data.groupedData[index]?.average) || 0;
              return (
                <div key={fieldItem.ID} id="scale-visualisation-scale-item-container">
                  <div className="scale-visualisation-label-container">
                    <p>{fieldItem.ReachStart}</p>
                    <p>{scaleStartLabel(fieldItem)}</p>
                  </div>

                  <div id="scale-visualisation-scale-box-container">
                    <div
                      id="scale-visualisation-average-score-container"
                      style={{ width: `${calculateWidthPercentage(averageScore, fieldItem.ReachStart, fieldItem.ReachEnd)}%` }}
                    >
                      <div id="scale-visualisation-avarage-score-item-container">
                        {showProjectResults ? (
                          <Tooltip content={projectResults()} top='80px' width='30px' left='-50px'>
                            <h1 style={{ color: h1Color }}>
                              {averageScore.toFixed(1)}
                            </h1>
                          </Tooltip>
                        ) : (
                          <h1 style={{ color: h1Color }}>
                            {averageScore.toFixed(1)}
                          </h1>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="scale-visualisation-label-container">
                    <p>{fieldItem.ReachEnd}</p>
                    <p>{fieldItem.ReachEndLabel}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : <AnalyticsEmptyState />}
    </>
  );
};

export default ScaleMomentVisualisation;
